'use client';

import React, { useState } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import PlusIcon from '../atoms/icons/plus';
import UtilityButton from '../buttons/utilityButton';
import ButtonOther from '../buttons/button_other';
import { useTranslation } from 'react-i18next';
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from 'react-zoom-pan-pinch';
import { getResponsiveImageUrl } from '../../utils/imageUrls';

// Control component for react-zoom-pan-pinch (ski maps) --- --- --- --- --->
const Controls = ({ children }) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="w-full max-w-screen-xl relative">
      <div className="tools absolute z-10 flex w-full p-1 gap-x-1 sm:gap-x-2">
        <ButtonOther
          classes={`rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-fit h-8 px-4 py-2 hidden sm:flex justify-center items-center`}
          onClick={(e) => {
            zoomIn();
            e.stopPropagation();
          }}
          label="Zoom in +"
          ariaLabel="Zoom in"
        />
        <ButtonOther
          classes={`flex items-center justify-center rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-8 h-8 sm:hidden`}
          onClick={(e) => {
            zoomIn();
            e.stopPropagation();
          }}
          label=""
          icon={<PlusIcon />}
          ariaLabel="Zoom in"
        />
        <ButtonOther
          classes={`rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-fit h-8 px-4 py-2 hidden sm:flex justify-center items-center`}
          onClick={(e) => {
            zoomOut();
            e.stopPropagation();
          }}
          label="Zoom out -"
          ariaLabel="Zoom out"
        />
        <ButtonOther
          classes={`flex items-center justify-center rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-8 h-8 sm:hidden`}
          onClick={(e) => {
            zoomOut();
            e.stopPropagation();
          }}
          label=""
          icon={<PlusIcon inverted={true} />}
          ariaLabel="Zoom out"
        />
        <ButtonOther
          classes={`rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-fit h-8 px-4 py-2 flex justify-center items-center`}
          onClick={(e) => {
            resetTransform();
            e.stopPropagation();
          }}
          label="Reset"
          ariaLabel="Reset image size"
        />
        {children}
      </div>
    </div>
  );
};
// >--- --- --- --- --- Control component for react-zoom-pan-pinch (ski maps)

const Image = ({
  blok,
  inSlider = false,
  itemSliderPosition = 0,
  numberOfSlides = 0,
  parentWidth,
}) => {
  // TailwindCSS utility classes --- --- --- --- --->

  const widths = {
    'w-1/4': 'col-span-12 sm:col-span-6 md:col-span-3 w-full sm:w-1/2 md:w-1/4',
    'w-1/3': 'col-span-12 sm:col-span-6 md:col-span-4 w-full sm:w-1/2 md:w-1/3',
    'w-1/2': 'col-span-12 md:col-span-6 w-full sm:w-1/2',
    'w-2/3': 'col-span-12 sm:col-span-8 w-full sm:w-2/3',
    'w-3/4': 'col-span-12 sm:col-span-9 w-full sm:w-3/4',
    'w-full': 'col-span-12 w-full',
  };

  const imagePosition = {
    topLeft: 'object-left-top',
    left: 'object-left',
    bottomLeft: 'object-left-bottom',
    centerLeftTop: 'object-[25%_0%]',
    centerLeft: 'object-[25%_50%]',
    centerLeftBottom: 'object-[25%_100%]',
    topCenter: 'object-top',
    center: 'object-center',
    bottomCenter: 'object-bottom',
    centerRightTop: 'object-[75%_0%]',
    centerRight: 'object-[75%_50%]',
    centerRightBottom: 'object-[75%_100%]',
    topRight: 'object-right-top',
    right: 'object-right',
    bottomRight: 'object-right-bottom',
  };
  // >--- --- --- --- --- TailwindCSS utility classes

  const smartResize =
    blok.image && blok.image.filename
      ? blok.image.filename.indexOf('a.storyblok.com') > -1
      : false;

  const width = widths[blok.width] || widths['w-full'];

  const [showFullscreen, setShowFullscreen] = useState(false);

  const { t } = useTranslation('image');
  const imageUrlParams = {
    filename: blok.image.filename,
    imageId: blok.qbank_id,
    parentWidth: parentWidth,
    inSlider: inSlider,
    forceAspect: blok.forceAspect || inSlider,
  };

  if (blok.zoomable) {
    return (
      <>
        <div
          className={`${width} flex-shrink-0 ${
            inSlider ? 'min-h-[90vh]' : ''
          } cursor-zoom-in relative`}
          {...storyblokEditable(blok)}
          onClick={blok.zoomable ? setShowFullscreen : null}
          role="button"
          tabIndex="0"
          aria-label={blok.description || t('Zoom in')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowFullscreen();
            }
          }}
        >
          <div className="flex inset bg-black/30 w-full h-full absolute rounded items-end px-2 md:px-4">
            <UtilityButton
              onClick={blok.zoomable ? setShowFullscreen : null}
              label={blok.description || t('Zoom in')}
              icon={<PlusIcon />}
            />
          </div>
          <picture className={`w-full`}>
            <source
              media="(max-width: 640px)"
              srcSet={`${blok.image.filename}${
                smartResize ? (inSlider ? '/m/640x640/' : '/m/640x640/') : ''
              }`}
            />
            <source
              media="(max-width: 768px)"
              srcSet={`${blok.image.filename}${
                smartResize ? (inSlider ? '/m/768x768/' : '/m/768x768/') : ''
              }`}
            />
            <source
              media="(max-width: 1024px)"
              srcSet={`${blok.image.filename}${
                smartResize ? (inSlider ? '/m/1024x768/' : '/m/1024x768/') : ''
              }`}
            />
            <source
              media="(min-width: 1025px)"
              srcSet={`${blok.image.filename}${
                smartResize ? '/m/1440x1024' : ''
              }`}
            />
            <img
              loading={
                !inSlider ||
                (inSlider &&
                  (itemSliderPosition > 3 ||
                    itemSliderPosition < numberOfSlides - 1))
                  ? 'lazy'
                  : undefined
              }
              src={`${blok.image.filename}${smartResize ? '/m/' : ''}`}
              alt={blok.image.alt}
              className={`object-cover object-center ${
                inSlider
                  ? 'w-full min-h-[70vh] sm:min-h-[80vh] md:min-h-full'
                  : 'w-full h-auto'
              } ${imagePosition[blok.image_position]} rounded`}
            />
          </picture>
        </div>
        <div
          // ref={fullScreenRef}
          className={`${
            showFullscreen
              ? 'flex flex-col justify-center items-center'
              : 'hidden'
          } fixed z-110 inset-0 bg-black bg-opacity-50 transition-opacity overflow-auto overscroll-contain px-2 md:px-4`}
        >
          {/* react-zoom-pan-pinch library --- --- --- --- ---> */}

          <TransformWrapper
            initialScale={1}
            initialPositionX={0}
            initialPositionY={0}
            disablePadding={true}
            onPinchingStart={(e) => e.stopPropagation()}
            onPinching={(e) => e.stopPropagation()}
            onPinchingStop={(e) => e.stopPropagation()}
          >
            {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
              <>
                <Controls>
                  <ButtonOther
                    classes={`flex items-center justify-center rounded-full bg-yellow-60 transition-colors hover:bg-yellow-40 w-8 h-8 ml-auto rotate-45`}
                    onClick={(e) => {
                      setShowFullscreen(false);
                      resetTransform();
                      e.stopPropagation();
                    }}
                    label=""
                    icon={<PlusIcon />}
                    ariaLabel="Close zoomable image"
                  />
                </Controls>
                <TransformComponent>
                  <img
                    src={`${blok.image.filename}${smartResize ? '/m/' : ''}`}
                    alt={blok.image.alt}
                    className={`rounded w-full max-w-screen-xl`}
                  />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
      </>
    );
  } else
    return (
      <picture
        className={`${width} flex-shrink-0 h-full`}
        {...storyblokEditable(blok)}
      >
        <source
          media="(max-width: 640px)"
          srcSet={getResponsiveImageUrl({
            screenWidth: '640px',
            ...imageUrlParams,
          })}
        />
        <source
          media="(max-width: 768px)"
          srcSet={getResponsiveImageUrl({
            screenWidth: '768px',
            ...imageUrlParams,
          })}
        />
        <source
          media="(max-width: 1024px)"
          srcSet={getResponsiveImageUrl({
            screenWidth: '1024px',
            ...imageUrlParams,
          })}
        />
        <source
          media="(min-width: 1024px)"
          srcSet={getResponsiveImageUrl({
            screenWidth: '1440px',
            ...imageUrlParams,
          })}
        />
        <img
          loading="lazy"
          src={getResponsiveImageUrl({
            screenWidth: '640px',
            ...imageUrlParams,
          })}
          alt={blok.image.alt}
          className={`object-cover w-full h-full ${
            imagePosition[blok.image_position] || 'object-center'
          } rounded`}
        />
      </picture>
    );
};

export default Image;
